import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Cookie from "js-cookie";
import { DashboardClientSummary } from "./DashboardClientSummary";
import { DashboardSites } from "./DashboardSites";
import { DashboardCampaigns } from "./DashboardCampaigns";
import { DashboardCharacterizations } from "./DashboardCharacterizations";
import { DashboardVolumeHighlights } from "./DashboardVolumeHighlights";
import { DashboardSortingErrors } from "./DashboardSortingErrors";
import { DashboardRotations } from "./DashboardRotations";
import { DashboardCharacterizationReports } from "./DashboardCharacterizationReports";
import { DashboardDisplayRulesModal } from "./DashboardDisplayRulesModal";
import { getStoredUserRole } from "./dashboardDisplayRules";
import HowItWorksButton from "../../components/howItWorks/HowItWorksButton";
import { useTranslation } from "react-i18next";
import { PeriodFilterModal } from "../../ui/components/filters";
import { useDashboardPeriodStats } from "./useDashboardPeriodStats";
import {
  buildDashboardSiteZoneCounts,
  errorsPerRotation,
  shouldHideDashboardRotations,
} from "./dashboardKpiUtils";
import { getSuperContainerList } from "../../services/container";
import { useIsAdminViewMode } from "../../hooks/useViewMode";
import PillButton from "../../ui/components/PillButton";
import {
  resolveInitialDashboardPeriod,
  writeStoredDashboardPeriod,
} from "./dashboardPeriod";

export const DashboardIndicators = ({ corporation, welcomeTitle }) => {
  const API = process.env.REACT_APP_API_BACKEND;
  const token = Cookie.get("userToken");
  const { t } = useTranslation();
  const isAdmin = useIsAdminViewMode();

  const [isLoading, setIsLoading] = useState(true);
  const [totalVolumeUsed, setTotalVolumeUsed] = useState(0);
  const [addresses, setAddresses] = useState([]);
  const [zoneCount, setZoneCount] = useState(0);
  const [activatedSortingErrorOption, setActivatedSortingErrorOption] =
    useState(false);
  const [showRotations, setShowRotations] = useState(false);
  const [initialPeriod] = useState(() => resolveInitialDashboardPeriod());
  const [periodFilter, setPeriodFilter] = useState(initialPeriod.periodFilter);
  const [periodStart, setPeriodStart] = useState(initialPeriod.periodStart);
  const [periodStop, setPeriodStop] = useState(initialPeriod.periodStop);
  const [periodDisplayLabel, setPeriodDisplayLabel] = useState(
    () => initialPeriod.periodDisplayLabel || t("Last 3 months"),
  );
  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [isDisplayRulesOpen, setIsDisplayRulesOpen] = useState(false);

  const {
    analyzedVolume,
    rotationCurrentCount,
    rotationPreviousCount,
    collectionDataResult,
    collectionPreviousDataResult,
    collectionsReady,
    sortingCurrentCount,
    sortingPreviousCount,
    sortingDataResult,
    sortingReady,
    hasComparablePreviousSorting,
  } = useDashboardPeriodStats(
    periodStart,
    periodStop,
    activatedSortingErrorOption,
  );

  const corporationModules = JSON.parse(
    localStorage.getItem("corporationModules"),
  );
  const showCharacterizationModule = Boolean(
    corporationModules?.showCharacterizationModule,
  );
  const showCampaignModule = Boolean(corporationModules?.showCampaignModule);
  const monitoringModuleEnabled = Boolean(
    corporationModules?.showMonitoringModule,
  );

  const fetchData = useCallback(() => {
    Promise.all([
      axios.get(`${API}dashboard`, {
        headers: { authorization: `Bearer ${token}` },
      }),
      getSuperContainerList().catch(() => ({
        data: { superContainers: [] },
      })),
    ]).then(([response, superResponse]) => {
      if (response.status === 200) {
        const lastDataContainers = response.data.lastDataContainers || [];
        const superContainers = superResponse?.data?.superContainers ?? [];
        const { addresses: nextAddresses, zoneCount: nextZoneCount } =
          buildDashboardSiteZoneCounts(lastDataContainers, superContainers);

        setActivatedSortingErrorOption(
          lastDataContainers.some(
            (container) => container.activatedSortingErrorOption,
          ),
        );
        setShowRotations(
          !shouldHideDashboardRotations({
            containers: lastDataContainers,
          }),
        );
        setAddresses(nextAddresses);
        setTotalVolumeUsed(response.data.totalVolumeUsed);
        setZoneCount(nextZoneCount);
        setIsLoading(false);
      }
    });
  }, [API, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const displayRulesContext = {
    monitoringModuleOn: monitoringModuleEnabled,
    characterizationModuleOn: showCharacterizationModule,
    campaignModuleOn: showCampaignModule,
    activatedSortingErrorOption,
    showRotations,
    hasSites: addresses.length > 0,
    isAdmin,
    userRole: getStoredUserRole(),
    showStorybookLink: Boolean(process.env.REACT_APP_STORYBOOK_URL) && isAdmin,
  };

  const corporationName =
    corporation?.corporationName || corporation?.name || "";
  const sortingStatsReady = sortingReady && collectionsReady;
  const sortingCurrentRate = sortingStatsReady
    ? errorsPerRotation(sortingCurrentCount, rotationCurrentCount)
    : null;
  const sortingPreviousRate = sortingStatsReady
    ? errorsPerRotation(sortingPreviousCount, rotationPreviousCount)
    : null;

  const applyPeriodFilter = (
    months,
    label,
    startDateIso,
    endDateIso,
    presetId,
  ) => {
    const nextFilter = months === undefined ? 3 : months;
    const nextLabel = label || t("Last 3 months");
    const nextStart = startDateIso || periodStart;
    const nextStop = endDateIso || periodStop;
    setPeriodFilter(nextFilter);
    setPeriodDisplayLabel(nextLabel);
    if (startDateIso && endDateIso) {
      setPeriodStart(startDateIso);
      setPeriodStop(endDateIso);
    }
    writeStoredDashboardPeriod({
      periodFilter: nextFilter,
      periodStart: nextStart,
      periodStop: nextStop,
      periodDisplayLabel: nextLabel,
      presetId: presetId ?? null,
    });
  };

  return (
    <section>
      <div className="dashboard-page-header">
        <h2 className="dashboard-page-header-title">{welcomeTitle}</h2>
        <HowItWorksButton topic="dashboard" context={displayRulesContext} />
      </div>
      <div className="dashboard-toolbar">
        <button
          type="button"
          className="search-input table-standard-filter-button"
          onClick={() => setIsPeriodModalOpen(true)}
        >
          <span>
            {t("Period")} :{" "}
            <span className="dashboard-period-label">
              {periodDisplayLabel || t("Last 3 months")}
            </span>
          </span>
          <i
            className="fas fa-chevron-down table-standard-filter-icon"
            aria-hidden
          />
        </button>
        {isAdmin ? (
          <PillButton size="sm" onClick={() => setIsDisplayRulesOpen(true)}>
            {t("dashboard.displayRules.open")}
          </PillButton>
        ) : null}
      </div>
      {isDisplayRulesOpen ? (
        <DashboardDisplayRulesModal
          isOpen={isDisplayRulesOpen}
          onClose={() => setIsDisplayRulesOpen(false)}
          context={displayRulesContext}
        />
      ) : null}
      {isPeriodModalOpen ? (
        <PeriodFilterModal
          isOpen={isPeriodModalOpen}
          onClose={() => setIsPeriodModalOpen(false)}
          onApply={applyPeriodFilter}
          currentPeriodFilter={periodFilter}
          currentPeriodStart={periodStart}
          currentPeriodEnd={periodStop}
        />
      ) : null}

      <div className="dashboard-layout">
        <div className="dashboard-layout-main">
          {monitoringModuleEnabled ? (
            <DashboardVolumeHighlights
              analyzedVolume={collectionsReady ? analyzedVolume : null}
              onSiteVolume={isLoading ? null : totalVolumeUsed}
            />
          ) : null}
          {!isLoading && activatedSortingErrorOption ? (
            <DashboardSortingErrors
              currentCount={sortingCurrentRate}
              previousCount={sortingPreviousRate}
              dataResult={sortingDataResult}
              collectionDataResult={collectionDataResult}
              statsReady={sortingStatsReady}
              showPreviousComparison={hasComparablePreviousSorting}
            />
          ) : null}
          {!isLoading && showRotations ? (
            <DashboardRotations
              currentCount={collectionsReady ? rotationCurrentCount : null}
              previousCount={collectionsReady ? rotationPreviousCount : null}
              dataResult={collectionDataResult}
              previousDataResult={collectionPreviousDataResult}
              statsReady={collectionsReady}
            />
          ) : null}
          {showCharacterizationModule ? (
            <DashboardCharacterizationReports
              periodStart={periodStart}
              periodStop={periodStop}
              periodLabel={periodDisplayLabel || t("Last 3 months")}
            />
          ) : null}
        </div>
        <aside className="dashboard-layout-aside">
          <DashboardClientSummary
            corporationName={corporationName}
            siteCount={addresses.length}
            zoneCount={zoneCount}
          />
          {monitoringModuleEnabled ? (
            <DashboardSites addresses={addresses} />
          ) : null}
          {showCampaignModule ? <DashboardCampaigns /> : null}
          {showCharacterizationModule ? <DashboardCharacterizations /> : null}
        </aside>
      </div>
    </section>
  );
};
